import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';

import analytics from '../../shared/services/analytics/analytics';
import { COMPETITIONS, SIGN_IN, SIGN_UP } from '../../shared/constants';
import { FormattedMessageHelper } from '../../shared/helpers/internationalization';
import { getIsLoggedInSelector } from '../../reducers/AuthenticationReducer/AuthenticationSelector';
import { useFormState } from 'react-hook-form';

import * as Fields from '../../components/Account/Data/Fields';
import { InputField } from '../Account/FormFields/InputField';
import { PasswordGeneratorField } from '../Account/FormFields/PasswordGeneratorField';
import { CheckboxField } from '../Account/FormFields/CheckboxField';
import Agreements from '../../components/Agreements/Agreements';
import { addModifiers } from '../../shared/helpers';
import { PASSWORD_GUIDELINES } from '../../components/Forms/Password/PasswordGuidelines/PasswordGuidelines';
import { useAccountCreateForm } from '../Account/wizard/formHelpers';
import { logErrorService } from '../../shared/services';
import { webSimApiErrorFlashMessages } from '../../shared/helpers/fetch-helper';

import './AlphathonLanding.scss';

export interface AlphathonRegisterProps {
  competitionId: string;
}

type FieldValues = {
  email: string;
  password: string;
  competitionId: string;
  agreePrivacyPolicy: boolean;
};

const passwordGuidelines = PASSWORD_GUIDELINES.map(guideline =>
  guideline.content === 'step1.password.guidelines.similarToUserAttributes'
    ? {
        ...guideline,
        content: 'step1.password.guidelines.similarToEmail',
      }
    : guideline
);

const makeLinkToSignIn = (competitionId: string) => {
  const competitionPath = `${COMPETITIONS}/${competitionId}`;
  return (text: string) => <Link to={{ pathname: `${SIGN_IN}/${competitionId}`, state: { from: competitionPath } }}>{text}</Link>;
};

export function AlphathonRegister({ competitionId }: AlphathonRegisterProps) {
  const history = useHistory();

  const isLoggedIn = useSelector(getIsLoggedInSelector) as boolean;

  function makeClickEnrollHandler(competitionId: string) {
    const competitionPath = `${COMPETITIONS}/${competitionId}`;
    return () => {
      analytics.trackCustomEvent(`click_${competitionId}_page`, { event_label: 'open_competition' });
      history.push(competitionPath);
    };
  }

  const form = useAccountCreateForm();
  const dispatch = useDispatch();

  const onSubmit = (values: FieldValues) => {
    try {
      analytics.trackCustomEvent(`submit_${competitionId}_landing_form`);
      const signUpPath = `${SIGN_UP}/${competitionId}`;
      history.push(signUpPath, {
        [Fields.email.props.name]: values[Fields.email.props.name],
        [Fields.accountCreatePassword.props.name]: values[Fields.accountCreatePassword.props.name],
        [Fields.privacyPolicy.props.name]: values[Fields.privacyPolicy.props.name],
      });
    } catch (e) {
      logErrorService(e);
      webSimApiErrorFlashMessages(dispatch);
    }
  };

  const formState = useFormState({ control: form.control });

  if (isLoggedIn) {
    return (
      <div className="iqc__open-competition">
        <button onClick={makeClickEnrollHandler(competitionId)} className="iqc__join-button iqc__join-button--xl">
          <FormattedMessageHelper id="alphathon.openCompetition" />
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="iqc__join-form">
      <div className="iqc__join-form-field-list">
        <div className="iqc__join-form-field">
          <InputField
            inputProps={{ ...Fields.email.props, noValidate: true }}
            controllerProps={{
              control: form.control,
              name: Fields.email.props.name,
            }}
          />
        </div>

        <div className="iqc__join-form-field">
          <PasswordGeneratorField
            controllerProps={{
              control: form.control,
              name: Fields.accountCreatePassword.props.name,
              form,
            }}
            inputProps={{ guidelines: passwordGuidelines, autocomplete: Fields.accountCreatePassword.props.autocomplete }}
          />
        </div>

        <CheckboxField
          controllerProps={{
            control: form.control,
            name: Fields.privacyPolicy.props.name,
          }}
          inputProps={{
            value: Fields.privacyPolicy.props.name,
            className: addModifiers('iqc__join-form-field', ['span-2']),
            labelElement: (
              <div className="account-wizard__text">
                <FormattedMessageHelper
                  tagName="span"
                  id="step5.iHerebyAgree"
                  values={{
                    PrivacyPolicy: (messageId: string) => (
                      <Agreements
                        agreementsId="privacy-policy"
                        agreementType="agreement"
                        shouldTriggerModalClose={true}
                        modalBtnText={messageId}
                      />
                    ),
                  }}
                />
                <sup>*</sup>
              </div>
            ),
          }}
        />
      </div>

      <button
        type="submit"
        disabled={formState.isSubmitting}
        className="iqc__join-button iqc__join-button--xl"
        onClick={() => {
          analytics.trackCustomEvent(`click_${competitionId}_page`, { event_label: 'register_form_button' });
        }}
      >
        <FormattedMessageHelper id="iqc.registerButton" />
      </button>

      <p className="alphathon__register-button-hint">
        <small>
          <FormattedMessageHelper id="alphathon.already.have.account" values={{ alphathonLinkToSignIn: makeLinkToSignIn(competitionId) }} />
        </small>
      </p>
    </form>
  );
}
